import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import {
  FiltersDefinition,
  FiltersState,
  OneDataCollection,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import {
  AnalyticsReportResultsDocument,
  AnalyticsReportResultsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { datatableFiltersToJsonLogicFilters } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import {
  OneRecord,
  formatColumns,
} from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

import type { ReportTableAction, ReportTableProps } from './index'

interface ReportTableContentProps {
  id: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ReportTableProps['columnFormatHints']
  onStateChange: ReportTableProps['onStateChange']
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  columnsData: AnalyticsReportResultsQuery
  filterDefinitions: FiltersDefinition<string>
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const ReportTableContent = ({
  id,
  sqlQuery,
  tableId,
  actions,
  columnFormatHints,
  onStateChange,
  onUserFacingSqlQueryChange,
  columnsData,
  filterDefinitions,
  currentFilters,
}: ReportTableContentProps) => {
  const client = useApolloClient()
  const { company, access } = useCurrentSession()

  const source = useDataCollectionSource(
    {
      filters: filterDefinitions,
      currentFilters: currentFilters,
      primaryActions: () => actions?.primary,
      secondaryActions: actions?.secondary
        ? {
            expanded: 1,
            actions: () => actions.secondary,
          }
        : undefined,
      dataAdapter: {
        paginationType: 'pages',
        perPage: INITIAL_PAGE_SIZE,
        fetchData: (options) => {
          const pagination = options.pagination
          const perPage = pagination.perPage ?? INITIAL_PAGE_SIZE
          const currentPage = pagination.currentPage ?? INITIAL_PAGE

          const filters = options.filters
          const jsonLogicFilters = datatableFiltersToJsonLogicFilters(filters)

          return client
            .watchQuery({
              query: AnalyticsReportResultsDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                query: sqlQuery,
                companyId: company.id,
                page: currentPage,
                perPage: perPage,
                tableId: tableId,
                filters: JSON.stringify(jsonLogicFilters),
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              onUserFacingSqlQueryChange(
                data?.iris?.sqlResultsConnection?.nodes?.[0]?.sqlQuery ?? ''
              )

              return {
                loading: loading,
                error: error,
                data: {
                  records:
                    data?.iris.sqlResultsConnection.nodes.map(
                      (node) =>
                        node.values.reduce<OneRecord>((acc, curr) => {
                          acc[curr.name] = curr.value ?? ''
                          return acc
                        }, {}) ?? []
                    ) ?? [],
                  total: data?.iris.sqlResultsConnection.totalCount ?? 0,
                  currentPage: currentPage,
                  pagesCount: Math.ceil(
                    (data?.iris.sqlResultsConnection.totalCount ?? 0) / perPage
                  ),
                  perPage: perPage,
                  type: 'pages',
                },
              }
            })
        },
      },
    },
    [filterDefinitions, sqlQuery, company.id]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      id={`report-table-${id}/v1`}
      storage={false}
      onStateChange={onStateChange}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnReordering: true,
            allowColumnHiding: true,
            frozenColumns: 1,
            columns: formatColumns(
              columnsData?.iris.sqlResultsConnection.nodes || [],
              columnFormatHints,
              access.locale
            ),
          },
        },
      ]}
    />
  )
}

export default ReportTableContent
